<script setup>
import { computed } from 'vue'

import { useAccountingStore } from '@/stores/accounting.store'

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  documentType: {
    type: Number,
    required: false,
    default: null,
  },
})

const accountingStore = useAccountingStore()
const { documentPrintLayoutEnum: layoutEnum, printDocumentTypeEnum, printLogoPositionEnum, printTotalsStyleEnum } = accountingStore

const ROW_COUNT = 7

const headerKinds = Object.freeze({
  [layoutEnum.BOLD_BAND]: 'band',
  [layoutEnum.CINEMATIC]: 'band',
  [layoutEnum.SIDEBAR]: 'sidebar',
  [layoutEnum.SPLIT_HEADER]: 'split',
  [layoutEnum.BOUTIQUE]: 'hero',
})
const logoPositions = Object.freeze({
  [printLogoPositionEnum.LEFT]: 'left',
  [printLogoPositionEnum.CENTER]: 'center',
  [printLogoPositionEnum.RIGHT]: 'right',
})
const totalsStyles = Object.freeze({
  [printTotalsStyleEnum.LINE]: 'line',
  [printTotalsStyleEnum.BOX]: 'box',
  [printTotalsStyleEnum.BAND]: 'band',
})

const isInvoice = computed(() => props.documentType === printDocumentTypeEnum.INVOICE)
const sheetClass = computed(() => [
  `header-${headerKinds[props.template.layout] ?? 'standard'}`,
  `logo-${logoPositions[props.template.logo_position]}`,
  `totals-${totalsStyles[props.template.totals_style]}`,
  {
    dark: props.template.layout === layoutEnum.CINEMATIC,
    zebra: props.template.zebra_rows,
    dense: props.template.layout === layoutEnum.RENTAL_COMPACT,
    grid: props.template.layout === layoutEnum.LEDGER,
  },
])
</script>
<template>
  <div class="print-template-thumbnail" :style="{ '--accent': template.accent_color }">
    <div class="sheet" :class="sheetClass">
      <div class="head">
        <span class="logo" />
        <span class="head-text">
          <span class="title" />
          <span class="subtitle" />
        </span>
      </div>
      <span class="rule" />
      <span class="table-head" />
      <span v-for="row in ROW_COUNT" :key="row" class="row" />
      <span class="totals" />
      <span v-if="isInvoice && template.highlight_payment_details" class="payment" />
    </div>
  </div>
</template>
<style scoped lang="scss">
.print-template-thumbnail {
  container-type: inline-size;
  width: 100%;
}

.sheet {
  --soft: color-mix(in srgb, var(--accent) 20%, white);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2.5cqw;
  aspect-ratio: 210 / 297;
  padding: 9cqw;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .12);
}

.head {
  display: flex;
  align-items: center;
  gap: 4cqw;
}

.head-text {
  display: flex;
  flex-direction: column;
  gap: 1.5cqw;
}

.logo {
  flex-shrink: 0;
  width: 16cqw;
  height: 11cqw;
  border-radius: 1cqw;
  background-color: var(--accent);
}

.title {
  width: 36cqw;
  height: 4cqw;
  border-radius: 1cqw;
  background-color: var(--accent);
}

.subtitle {
  width: 24cqw;
  height: 2.5cqw;
  border-radius: 1cqw;
  background-color: #bdbdbd;
}

.rule {
  height: .8cqw;
  margin: 1cqw 0;
  background-color: var(--accent);
}

.table-head {
  height: 3.5cqw;
  border-radius: .5cqw;
  background-color: var(--soft);
}

.row {
  height: 2.5cqw;
  border-radius: .5cqw;
  background-color: #e8e8e8;
}

.totals {
  align-self: flex-end;
  width: 46cqw;
  height: 9cqw;
  margin-top: 2cqw;
  border-top: .8cqw solid var(--accent);
}

.payment {
  width: 55cqw;
  height: 7cqw;
  border: .6cqw solid var(--accent);
  border-radius: 1cqw;
  background-color: var(--soft);
}

.logo-center .head,
.header-hero .head {
  flex-direction: column;
  text-align: center;
}

.logo-right .head {
  flex-direction: row-reverse;
}

.header-band .head {
  margin: -9cqw -9cqw 0;
  padding: 7cqw 9cqw;
  background-color: var(--accent);

  .logo,
  .title {
    background-color: #fff;
  }
}

.header-band .rule,
.header-split .rule,
.header-sidebar .rule,
.header-hero .rule {
  display: none;
}

.dark .head {
  background-color: #121212;
  border-top: 1.5cqw solid var(--accent);
  border-bottom: 1.5cqw solid var(--accent);

  .logo,
  .title {
    background-color: var(--accent);
  }
}

.dark .table-head {
  background-color: #121212;
}

.header-split .head-text {
  flex: 1;
  padding: 3cqw;
  border-radius: 1.5cqw;
  background-color: var(--soft);
}

.header-sidebar .head {
  flex-direction: column;
  align-items: flex-start;
  width: 38cqw;
  margin: -9cqw 0 0 -9cqw;
  padding: 9cqw 5cqw 5cqw;
  background-color: var(--accent);

  .logo,
  .title {
    background-color: #fff;
  }
}

.header-hero .head {
  padding: 5cqw;
  border-radius: 4cqw;
  background-color: var(--soft);
}

.totals-box .totals {
  border: .6cqw solid var(--accent);
  border-radius: 1cqw;
}

.totals-band .totals {
  border-top: none;
  border-bottom: 3cqw solid var(--accent);
}

.zebra .row:nth-of-type(even) {
  background-color: #cfcfcf;
}

.dense {
  gap: 1.5cqw;
}

.grid .row,
.grid .table-head {
  border-radius: 0;
  box-shadow: 0 0 0 .3cqw #9e9e9e;
}
</style>
